import { useNavigate } from "react-router-dom";
import { useContext } from "react";

import createLink from "../libs/createLink";
import { ModalContext } from "../ui/components2/ModalManager";
import useAbility from "./useAbility";
import useCurrentUser from "./useCurrentUser";
import { useDrawer } from "../ui/components2/DrawerProvider";
import ContentLocked from "../ui/components2/ContentLocked/ContentLocked";
import CONTENT_RESTRICTION_RULE from "../const/contentRestrictionRule";
import USER_ROLES from "../const/userRoles";
import CertificationModal from "../ui/components2/CertificationModal/CertificationModal";

export const CURRENT_USER_LOCKED_LABEL = {
  ROLE_GUEST: "ROLE_GUEST",
  ROLE_EMAIL_UNCONFIRMED: "ROLE_EMAIL_UNCONFIRMED",
  ROLE_BANNED: "ROLE_BANNED",
  ROLE_NO_ACCESS: "ROLE_NO_ACCESS",
  ROLE_NO_PREMIUM: "ROLE_NO_PREMIUM",
  USER_WARNING: "USER_WARNING",
  ROLE_CAN_BE_PRIVILEGED: "ROLE_CAN_BE_PRIVILEGED",
};

/**
 * ✅ Liste des actions qui doivent être Premium-only (isPremium)
 * Tout le reste ne doit dépendre QUE de hasAccess (accès global).
 *
 * Tu peux ajouter ici au fur et à mesure.
 */
const PREMIUM_ONLY_ACTIONS = new Set([
  CONTENT_RESTRICTION_RULE.REPOST_CONTENT,
  CONTENT_RESTRICTION_RULE.CREATE_POLL,
]);

const MODERATED_RESTRICTION_CHECK = {
  [CONTENT_RESTRICTION_RULE.CREATE_PUBLICATION_MEDIA]: (user) => {
    return !!user?.restrictions?.blockMediaCreation;
  },
};

export const useCurrentUserLockedState = ({ action }) => {
  const navigate = useNavigate();
  const { openDrawer, closeDrawer } = useDrawer();
  const currentUser = useCurrentUser();
  const currentRole = currentUser?.role || "guest";
  const { openModal } = useContext(ModalContext);

  const isPremiumOnlyAction = PREMIUM_ONLY_ACTIONS.has(action);

  const currentUserLockConditions = [
    // Guest
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_GUEST,
      condition: (user, role) => !user?._id && role === "guest",
      defaultHandler: (contentOwner, lockedAction) => {
        openModal("Signin", {
          lockedContentOwner: contentOwner,
          lockedReason: CURRENT_USER_LOCKED_LABEL.ROLE_GUEST,
          lockedAction,
        });
      },
    },

    // User warning / moderated restrictions
    {
      label: CURRENT_USER_LOCKED_LABEL.USER_WARNING,
      condition: (user) =>
        user?.restrictions?.blockAllContentCreation ||
        MODERATED_RESTRICTION_CHECK[action]?.(user),
      defaultHandler: () => {
        openModal("ModalUserRestrictions", {
          toggle: true,
          isModal: true,
          userId: currentUser?._id,
          username: currentUser?.username,
        });
      },
    },

    // Email unconfirmed
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_EMAIL_UNCONFIRMED,
      condition: (_user, role) => role === "emailUnconfirmed",
      defaultHandler: () => {
        openModal("EncourageEmailConfirm", { forceOpen: true });
      },
    },

    // Banned
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_BANNED,
      condition: (_user, role) => role === "banned",
      defaultHandler: () => {
        navigate(createLink("signin"));
      },
    },

    // Can be privileged (certif) — typiquement quand pas d'accès
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_CAN_BE_PRIVILEGED,
      condition: (user) =>
        [2, 3].includes(user?.profile?.gender) && !user?.subscription?.hasAccess,
      defaultHandler: () => {
        openDrawer(<CertificationModal handleCancel={closeDrawer} />, {
          small: true,
        });
      },
    },

    /**
     * ✅ Accès global: hasAccess
     * -> s'applique pour toutes les actions, y compris premium-only
     * (si pas d'accès, on bloque avant de parler de premium)
     */
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_NO_ACCESS,
      condition: (user) => !user?.subscription?.hasAccess,
      defaultHandler: () => {
        navigate(createLink("subscription", currentUser));
      },
    },

    /**
     * ✅ Premium: isPremium
     * -> ne s'applique QUE pour les actions premium-only
     * Donc aucun impact sur le reste du code.
     */
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_NO_PREMIUM,
      condition: (user) => isPremiumOnlyAction && !user?.subscription?.isPremium,
      defaultHandler: () => {
        navigate(createLink("subscription", currentUser));
      },
    },
  ];

  return currentUserLockConditions.find(({ condition }) =>
    condition(currentUser, currentRole)
  );
};

const useRestriction = (action, context, options = {}) => {
  const { cannot } = useAbility();
  const { openDrawer, closeDrawer } = useDrawer();
  const currentUser = useCurrentUser();
  const { openModal } = useContext(ModalContext);
  const currentUserLockedState = useCurrentUserLockedState({ action });

  const { restrictcionDisabled = false } = options;
  if (restrictcionDisabled) return { isRestricted: false };

  const label = currentUserLockedState?.label;
  const isUserSignupAfterNewRestriction =
    currentUser && new Date("2024-10-26") < new Date(currentUser.createdAt);
  const isEmailUnconfirmed = currentUser?.role === USER_ROLES.EMAIL_UNCONFIRMED;

  const isPaywallLocked = [
    CURRENT_USER_LOCKED_LABEL.ROLE_CAN_BE_PRIVILEGED,
    CURRENT_USER_LOCKED_LABEL.ROLE_NO_ACCESS,
    CURRENT_USER_LOCKED_LABEL.ROLE_NO_PREMIUM, // ne peut exister que sur actions premium-only
  ].includes(label);

  const canBePriviliged = label === CURRENT_USER_LOCKED_LABEL.ROLE_CAN_BE_PRIVILEGED;
  const isCertified = currentUser?.isCertified;

  const callDefaultHandler = (...args) => {
    if (currentUserLockedState?.defaultHandler) {
      currentUserLockedState.defaultHandler(...args);
    }
  };

  const handlers = {
    [CONTENT_RESTRICTION_RULE.CREATE_PUBLICATION_MEDIA]: () => {
      const cannotUploadMedia = cannot({ action: "upload", subject: "Media" });

      // locks "auth/modération" seulement
      const localLockedState = [
        CURRENT_USER_LOCKED_LABEL.ROLE_GUEST,
        CURRENT_USER_LOCKED_LABEL.USER_WARNING,
        CURRENT_USER_LOCKED_LABEL.ROLE_EMAIL_UNCONFIRMED,
        CURRENT_USER_LOCKED_LABEL.ROLE_BANNED,
      ].includes(label);

      const isRestricted = localLockedState || cannotUploadMedia;

      return {
        isRestricted,
        restrictionReason: label || (cannotUploadMedia ? "CANNOT_UPLOAD_MEDIA" : undefined),
        restrictionHandler: () => {
          if (localLockedState) {
            callDefaultHandler();
            return;
          }

          // lock CASL upload media (si tu veux un drawer dédié)
          if (cannotUploadMedia) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.CREATE_PUBLICATION_MEDIA}
                context={{ caslLocked: true }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
          }
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.ADD_TO_CIRCLE]: (contentOwner) => {
      const isRestricted = cannot({ action: "create", subject: "Friend" }) || !!currentUserLockedState;

      return {
        isRestricted,
        restrictionReason: label || "CANNOT_CREATE_FRIEND",
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.ADD_TO_CIRCLE}
                context={{ canBePriviliged, isCertified }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
            return;
          }

          callDefaultHandler(contentOwner, action);
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.MEMBERS_FILTER]: () => {
      const isRestricted = isEmailUnconfirmed || isPaywallLocked;

      return {
        isRestricted,
        restrictionReason: "CANNOT_FILTER_MEMBERS",
        restrictionHandler: (e) => {
          if (isEmailUnconfirmed) {
            if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
            openModal("EncourageEmailConfirm", { forceOpen: true });
            return;
          }

          if (isPaywallLocked) {
            if (isRestricted &&  e?.nativeEvent instanceof Event) e.preventDefault();
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.MEMBERS_FILTER}
                context={{ canBePriviliged, isCertified }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
          }
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.ENCOURAGE_GAME]: () => {
      return {
        isRestricted: isEmailUnconfirmed,
        restrictionReason: isEmailUnconfirmed
          ? CURRENT_USER_LOCKED_LABEL.ROLE_EMAIL_UNCONFIRMED
          : undefined,
        restrictionHandler: () => {
          if (isEmailUnconfirmed) {
            openModal("EncourageEmailConfirm", { forceOpen: true });
          }
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.MESSENGER]: (contentOwner = {}) => {
      const isOwnerAdminOrMod = [USER_ROLES.ADMINISTRATOR, USER_ROLES.MODERATOR].includes(
        contentOwner?.role
      );

      const isRestricted = !isOwnerAdminOrMod && !!currentUserLockedState;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.MESSENGER}
                context={{ ...contentOwner, canBePriviliged, isCertified }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
            return;
          }

          callDefaultHandler(contentOwner, action);
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.VIEW_LAST_VISITORS]: () => {
      const isRestricted = !!currentUserLockedState && isUserSignupAfterNewRestriction;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: () => {
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.VIEW_LAST_VISITORS}
                context={{ canBePriviliged, isCertified }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
            return;
          }

          callDefaultHandler();
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.CHAT_WEBCAM]: () => {
      const isRestricted = !!currentUserLockedState;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: () => {
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.CHAT_WEBCAM}
                context={{ canBePriviliged, isCertified }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
            return;
          }

          callDefaultHandler();
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.VIDEO_DURATION_LIMIT]: ({ media }) => {
      if (media?.owner?._id === currentUser?._id || !!media?.owner?.isGold) {
        return { isRestricted: false };
      }

      if (currentUser && new Date(currentUser.createdAt) < new Date("2024-01-01")) {
        return { isRestricted: false };
      }

      const videoAgeRestrictionDays = 30;
      const videoCreatedAt = new Date(media?.createdAt);
      const restrictionCutoff = new Date();
      restrictionCutoff.setDate(restrictionCutoff.getDate() - videoAgeRestrictionDays);

      const isVideoOlderThanRestriction = videoCreatedAt < restrictionCutoff;
      if (!isVideoOlderThanRestriction) return { isRestricted: false };

      const isGuestRestricted = label === CURRENT_USER_LOCKED_LABEL.ROLE_GUEST;

      // ⚠️ ici, tu avais un gate "premium/access" : on le garde sur l'accès global
      const isAccessRestricted = label === CURRENT_USER_LOCKED_LABEL.ROLE_NO_ACCESS;

      const isRestricted = isGuestRestricted || isAccessRestricted;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: () => {
          openDrawer(
            <ContentLocked
              restrictionRule={CONTENT_RESTRICTION_RULE.VIDEO_DURATION_LIMIT}
              context={{ videoAgeRestrictionDays, premium: isAccessRestricted }}
              inDrawer
              handleCancel={closeDrawer}
            />,
            { small: true }
          );
        },
        restrictionParams: {
          duration: 15,
          videoAgeRestrictionDays,
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.USER_BOOKMARKS_LIMIT]: () => {
      const bookmarksLimit = 20;
      const isRestricted =
        label === CURRENT_USER_LOCKED_LABEL.ROLE_NO_ACCESS && isUserSignupAfterNewRestriction;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionParams: { limit: bookmarksLimit },
      };
    },

    [CONTENT_RESTRICTION_RULE.USER_NOTIFICATIONS_LIMIT]: () => {
      const notificationLimit = 10;
      const isRestricted =
        label === CURRENT_USER_LOCKED_LABEL.ROLE_NO_ACCESS && isUserSignupAfterNewRestriction;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionParams: { limit: notificationLimit },
      };
    },

    [CONTENT_RESTRICTION_RULE.PROFIL_FEED_PUBLICATIONS_LIMIT]: ({ contentOwner }) => {
      if (!contentOwner || contentOwner?._id === currentUser?._id) return { isRestricted: false };

      const publicationLimitPremium = 40;
      const publicationLimitGuest = 10;

      const guestLocked = label === CURRENT_USER_LOCKED_LABEL.ROLE_GUEST;

      return {
        isRestricted: false, // tu l’avais désactivé
        restrictionReason: label,
        restrictionParams: {
          limit: guestLocked ? publicationLimitGuest : publicationLimitPremium,
          isGuestLocked: guestLocked,
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.PROFILE_MEDIAS_LIMIT]: ({ contentOwner }) => {
      if (!contentOwner || contentOwner?._id === currentUser?._id) return { isRestricted: false };

      const publicationLimitPremium = 20;
      const publicationLimitGuest = 10;

      const guestLocked = label === CURRENT_USER_LOCKED_LABEL.ROLE_GUEST;

      return {
        isRestricted: false, // tu l’avais désactivé
        restrictionReason: label,
        restrictionParams: {
          limit: guestLocked ? publicationLimitGuest : publicationLimitPremium,
          isGuestLocked: guestLocked,
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.PROFIL_COLLECTION_MEDIAS_LIMIT]: ({ contentOwner }) => {
      if (!contentOwner || contentOwner?._id === currentUser?._id) return { isRestricted: false };

      const publicationLimitGuest = 10;
      const guestLocked = label === CURRENT_USER_LOCKED_LABEL.ROLE_GUEST;

      return {
        isRestricted: false, // tu l’avais désactivé
        restrictionReason: label,
        restrictionParams: {
          limit: publicationLimitGuest,
          isGuestLocked: guestLocked,
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.FOLLOW_MEMBER]: (contentOwner = {}) => {
      const isGuest = !currentUser?._id;
      const onlyContributorsCanFollowMe = !!contentOwner?.setting?.onlyContributorsCanFollowMe;
      const contributorGate = onlyContributorsCanFollowMe && !currentUser?.isContributor;
      const isEmailUnconfirmedLocked =
        label === CURRENT_USER_LOCKED_LABEL.ROLE_EMAIL_UNCONFIRMED;

      const isRestricted = isGuest || isEmailUnconfirmedLocked || contributorGate;

      return {
        isRestricted,
        restrictionReason: isGuest
          ? CURRENT_USER_LOCKED_LABEL.ROLE_GUEST
          : contributorGate
            ? "CONTRIBUTOR_GATE"
            : undefined,
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!isRestricted) return;

          if (isGuest) {
            openModal("Signin", {
              lockedContentOwner: contentOwner,
              lockedReason: CURRENT_USER_LOCKED_LABEL.ROLE_GUEST,
              lockedAction: action,
            });
            return;
          }

          if (isEmailUnconfirmedLocked) {
            openModal("EncourageEmailConfirm", { forceOpen: true });
            return;
          }

          if (!contributorGate) return;

          openDrawer(
            <ContentLocked
              restrictionRule={CONTENT_RESTRICTION_RULE.FOLLOW_MEMBER}
              context={{ contributorGate: true, username: contentOwner?.username }}
              inDrawer
              handleCancel={closeDrawer}
            />,
            { small: true }
          );
        },
      };
    },

    /**
     * ✅ REPOST_CONTENT = Premium-only (si tu le veux)
     * - si pas hasAccess => ROLE_NO_ACCESS
     * - si hasAccess mais pas premium => ROLE_NO_PREMIUM
     * => aucun impact sur les hasAccess sauf si tu demandes premium (ici oui).
     */
    [CONTENT_RESTRICTION_RULE.REPOST_CONTENT]: ({ contentOwner } = {}) => {
      const isRestricted = !!currentUserLockedState;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.REPOST_CONTENT}
                context={{ username: contentOwner?.username }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
            return;
          }

          callDefaultHandler(contentOwner, action);
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.CREATE_POLL]: ({ contentOwner, enable = true } = {}) => {
      const isRestricted = enable ? !!currentUserLockedState : false;

      return {
        enable,
        isRestricted,
        restrictionReason: label,
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!enable) return;
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.CREATE_POLL}
                context={{ username: contentOwner?.username }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
            return;
          }

          callDefaultHandler(contentOwner, action);
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.BOOKMARK_MEDIA]: ({ media } = {}) => {
      const isGuest = !currentUser?._id;

      if (media?.isBookmarked) {
        return {
          isRestricted: false,
          restrictionReason: null,
          restrictionHandler: () => {},
        };
      }

      const onlyContributorsCanBookmark =
        (media?.canBookmark === false && media?.cannotBookmarkReason === "contributors_only") ||
        (media?.canBookmark === false && !media?.cannotBookmarkReason); // legacy

      const contributorGate = onlyContributorsCanBookmark && !currentUser?.isContributor;

      const ownerDisabledGate =
        media?.canBookmark === false &&
        media?.cannotBookmarkReason === "owner_disabled_favorites";

      const isCaslLocked = cannot({ action: "create", subject: "Bookmark" });

      const isRestricted = isGuest || ownerDisabledGate || contributorGate || isCaslLocked;

      const restrictionReason = isGuest
        ? CURRENT_USER_LOCKED_LABEL.ROLE_GUEST
        : ownerDisabledGate
          ? "OWNER_DISABLED_FAVORITES"
          : contributorGate
            ? "CONTRIBUTOR_GATE"
            : isCaslLocked
              ? "CANNOT_CREATE_BOOKMARK"
              : undefined;

      return {
        isRestricted,
        restrictionReason,
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!isRestricted) return;

          if (isGuest) {
            openModal("Signin", {
              lockedContentOwner: media?.owner,
              lockedReason: CURRENT_USER_LOCKED_LABEL.ROLE_GUEST,
              lockedAction: action,
            });
            return;
          }

          if (ownerDisabledGate) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.BOOKMARK_MEDIA}
                context={{ ownerDisabledFavorites: true, username: media?.owner?.username }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
            return;
          }

          if (contributorGate) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.BOOKMARK_MEDIA}
                context={{ contributorGate: true, username: media?.owner?.username }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
            return;
          }

          openDrawer(
            <ContentLocked
              restrictionRule={CONTENT_RESTRICTION_RULE.BOOKMARK_MEDIA}
              context={{ username: media?.owner?.username, caslLocked: true }}
              inDrawer
              handleCancel={closeDrawer}
            />,
            { small: true }
          );
        },
      };
    },
  };

  const restrictionHandler = handlers[action];
  if (restrictionHandler) {
    const currentHandler = restrictionHandler(context);
    if (currentHandler?.isRestricted) return currentHandler;
    return { ...currentHandler, isRestricted: false };
  }

  return { isRestricted: false };
};

export default useRestriction;